import React from 'react';
import axios from '../../utils/axiosConfig';

import Content from '../../components/Content';
import Loader from '../../components/Loader';

// Admin-only view of the append-only audit_log table. Every security-sensitive
// event (logins, account/appliance/ASM mutations) is written with a rich action
// name; every other state-changing API call lands here as a generic `api_call`
// row via the request-audit middleware — so this page shows every write against
// the C2C, whoever made it. The full request firehose (reads included) is in the
// rotating file log on the box (settings.access_log_path).
const PAGE_SIZE = 50;

const fmtTime = (iso) => {
	if (!iso) return '';
	try {
		return new Date(iso).toLocaleString();
	} catch {
		return String(iso);
	}
};

const AuditLogScreen = () => {
	const [ rows, setRows ] = React.useState([]);
	const [ loading, setLoading ] = React.useState(false);
	const [ error, setError ] = React.useState('');
	const [ page, setPage ] = React.useState(0);
	const [ actionFilter, setActionFilter ] = React.useState('');
	const [ actorFilter, setActorFilter ] = React.useState('');

	const load = React.useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
			if (actionFilter.trim()) params.action = actionFilter.trim();
			if (actorFilter.trim()) params.actor = actorFilter.trim();
			const { data } = await axios.get('/api/accounts/audit-log', { params });
			setRows(Array.isArray(data) ? data : []);
		} catch (e) {
			const status = e && e.response && e.response.status;
			const detail = e && e.response && e.response.data && e.response.data.detail;
			let msg;
			if (status === 403) msg = 'An admin role is required to view the audit log.';
			else if (detail) msg = String(detail);
			else if (status) msg = `Could not load the audit log (HTTP ${status}).`;
			else msg = 'Could not load the audit log — the server could not be reached.';
			setError(msg);
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, [ page, actionFilter, actorFilter ]);

	React.useEffect(() => {
		load();
	}, [ load ]);

	const onFilter = (e) => {
		e.preventDefault();
		setPage(0);
		load();
	};

	const cell = { padding: '8px 10px', borderBottom: '1px solid #26324a', verticalAlign: 'top', fontSize: '13px' };
	const head = { ...cell, textAlign: 'left', color: '#9fb3d1', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '2px solid #26324a' };

	return (
		<Content currentMenu='system' headerTitle='Audit Log'>
			<div style={{ padding: '16px 20px' }}>
				<p style={{ color: '#9fb3d1', fontSize: '13px', margin: '0 0 14px' }}>
					Every state-changing request against the C2C. Rich events (logins, account,
					appliance and surface changes) are named; other writes appear as{' '}
					<code>api_call</code>. The full request firehose, reads included, is in the
					rotating access log on the appliance.
				</p>

				<form onSubmit={onFilter} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
					<input
						type='text'
						placeholder='Filter by action (e.g. login_failed)'
						value={actionFilter}
						onChange={(e) => setActionFilter(e.target.value)}
						style={{ padding: '7px 10px', minWidth: '220px', background: '#0f1826', color: '#e6edf6', border: '1px solid #26324a', borderRadius: '6px' }}
					/>
					<input
						type='text'
						placeholder='Filter by user'
						value={actorFilter}
						onChange={(e) => setActorFilter(e.target.value)}
						style={{ padding: '7px 10px', minWidth: '180px', background: '#0f1826', color: '#e6edf6', border: '1px solid #26324a', borderRadius: '6px' }}
					/>
					<button type='submit' style={{ padding: '7px 16px', background: '#2f6fed', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
						Apply
					</button>
					<button
						type='button'
						onClick={() => { setActionFilter(''); setActorFilter(''); setPage(0); }}
						style={{ padding: '7px 16px', background: '#1b2740', color: '#cdd9ec', border: '1px solid #26324a', borderRadius: '6px', cursor: 'pointer' }}
					>
						Clear
					</button>
				</form>

				{error ? (
					<div style={{ color: '#ff8a8a', padding: '10px 0' }}>{error}</div>
				) : loading ? (
					<Loader />
				) : (
					<div style={{ overflowX: 'auto' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
							<thead>
								<tr>
									<th style={head}>Time</th>
									<th style={head}>User</th>
									<th style={head}>Action</th>
									<th style={head}>Resource</th>
									<th style={head}>IP</th>
									<th style={head}>Details</th>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 ? (
									<tr>
										<td style={cell} colSpan={6}>No audit events for this filter.</td>
									</tr>
								) : (
									rows.map((r) => (
										<tr key={r.id}>
											<td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmtTime(r.occurred_at)}</td>
											<td style={cell}>{r.actor_username || <span style={{ color: '#6b7a92' }}>—</span>}</td>
											<td style={{ ...cell, fontWeight: 600 }}>{r.action}</td>
											<td style={cell}>
												{r.resource_type || ''}
												{r.resource_id ? <span style={{ color: '#6b7a92' }}> #{r.resource_id}</span> : ''}
											</td>
											<td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.actor_ip || ''}</td>
											<td style={{ ...cell, fontFamily: 'monospace', color: '#9fb3d1', maxWidth: '380px', wordBreak: 'break-word' }}>
												{r.details || ''}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				)}

				<div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
					<button
						type='button'
						disabled={page === 0 || loading}
						onClick={() => setPage((p) => Math.max(0, p - 1))}
						style={{ padding: '6px 14px', background: '#1b2740', color: '#cdd9ec', border: '1px solid #26324a', borderRadius: '6px', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
					>
						Previous
					</button>
					<span style={{ color: '#9fb3d1', fontSize: '13px' }}>Page {page + 1}</span>
					<button
						type='button'
						disabled={rows.length < PAGE_SIZE || loading}
						onClick={() => setPage((p) => p + 1)}
						style={{ padding: '6px 14px', background: '#1b2740', color: '#cdd9ec', border: '1px solid #26324a', borderRadius: '6px', cursor: rows.length < PAGE_SIZE ? 'default' : 'pointer', opacity: rows.length < PAGE_SIZE ? 0.5 : 1 }}
					>
						Next
					</button>
				</div>
			</div>
		</Content>
	);
};

export default AuditLogScreen;
